/**
 * Os critérios de uma etapa, por expansão (E147.F3, ADR-0101 §2.1): cada um com o resultado, se é
 * crítico e o fundamento; a versão gravada (e se ela vale para a conferência de agora) e o
 * retrabalho e a espera da etapa (§2.6). Só leitura: o que se faz está na ação principal.
 */
import { Badge, type BadgeProps } from '@/components/ui';
import type { EtapaDoCasoData, ResultadoDoCriterio } from '@/lib/conferencia_das_etapas';
import { formatarDataHora } from '@/lib/trilha_format';

import { duracao, preencher, t, textoDoFundamento } from './textos';

const VARIANTE: Record<ResultadoDoCriterio, BadgeProps['variant']> = {
  passou: 'success',
  falhou: 'danger',
  inconclusivo: 'warning',
  nao_se_aplica: 'neutral',
};

function Versao({ etapa }: { etapa: EtapaDoCasoData }): JSX.Element {
  const gravada = etapa.gravada;
  if (gravada === null) return <p>{t.semVersao}</p>;
  const texto = etapa.vigente ? t.versaoGravada : t.versaoDeOutraRodada;
  return (
    <p data-testid="etapa-versao" data-vigente={etapa.vigente}>
      {preencher(texto, { versao: gravada.versao, quando: formatarDataHora(gravada.criada_em) })}
    </p>
  );
}

function Metricas({ etapa }: { etapa: EtapaDoCasoData }): JSX.Element {
  const m = etapa.metricas;
  return (
    <>
      <p data-testid="etapa-retrabalho">
        {preencher(t.retrabalho, {
          versoes: m.versoes,
          tentativas: m.tentativas,
          limitacoes: m.limitacoes,
        })}
      </p>
      {m.segundos_ate_a_aprovacao !== null ? (
        <p>{preencher(t.espera, { duracao: duracao(m.segundos_ate_a_aprovacao) })}</p>
      ) : null}
    </>
  );
}

export function CriteriosDaEtapa({ etapa }: { etapa: EtapaDoCasoData }): JSX.Element {
  const criterios = etapa.agora?.criterios ?? [];
  return (
    <div className="space-y-sc-2" data-testid="etapa-criterios">
      <ul className="space-y-1">
        {criterios.map((c, i) => (
          <li
            key={`${c.chave}-${c.objeto_id ?? i}-${i}`}
            className="flex flex-wrap items-baseline gap-sc-2 text-body-sm"
            data-criterio={c.chave}
            data-resultado={c.resultado}
          >
            <Badge variant={VARIANTE[c.resultado]}>{t.resultado[c.resultado]}</Badge>
            <span className="font-semibold text-fg">{t.criterio[c.chave]}</span>
            <span className="text-caption text-fg-muted">
              {c.critico ? t.critico : t.informativo}
            </span>
            <span className="text-fg-muted">{textoDoFundamento(c)}</span>
          </li>
        ))}
      </ul>
      <div className="space-y-1 text-caption text-fg-muted">
        <Versao etapa={etapa} />
        <Metricas etapa={etapa} />
      </div>
    </div>
  );
}
